import { AdvisorInfo, IAdvisorInfo } from './advisor-info.model';
import { Json } from '@/integrations/supabase/types';

/**
 * Status possibili per un itinerario
 */
export type ItineraryStatus = 'draft' | 'pending' | 'approved' | 'completed' | 'rejected' | 'sended' | 'expired' | 'request_changes';

/**
 * Status possibili per una travel request
 */
export type TravelRequestStatus = 'pending' | 'assigned' | 'completed' | 'rejected';

/**
 * Informazioni guest
 */
export interface IGuest {
  id: number;
  age: number;
}

/**
 * Informazioni di contatto del cliente embedded nell'itinerario
 */
export interface IContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

/**
 * Dati raw che arrivano dalla view complete_itineraries_with_users_v
 * Questa interfaccia mappa 1:1 i campi della view/RPC
 */
export interface IDashboardViewData {
  // Campi itinerary
  itinerary_id: string;
  travel_request_id: string;
  itinerary_title: string;
  itinerary_status: string;
  itinerary_start_date: string | null;
  itinerary_end_date: string | null;
  itinerary_guests_data: Json;
  itinerary_budget: number;
  itinerary_description: string;
  itinerary_image: string | null;
  itinerary_total: number | null;
  itinerary_note: string | null;
  itinerary_commisionable_total: number | null;
  itinerary_not_commisionable_total: number | null;
  itinerary_p2b_commission_percentage: number | null;
  itinerary_p2b_commission_amount: number | null;
  itinerary_td_fee_percentage: number | null;
  itinerary_created_at: string;
  itinerary_updated_at: string;

  // Campi travel_request
  travel_request_user_id: string | null;
  destinations: string[];
  departure_point: string | null;
  needs_transportation: boolean;
  total_guests: number;
  guests_data: Json;
  travel_dates_flexible: boolean;
  travel_start_date: string | null;
  travel_end_date: string | null;
  budget_min: number | null;
  budget_max: number | null;
  budget_flexible: boolean;
  interests: string[];
  additional_notes: string | null;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  travel_request_status: string;
  assigned_designer_id: string | null;
  travel_designer_preference: string | null;
  travel_request_created_at: string;
  travel_request_updated_at: string;

  // Campi travel advisor (assigned)
  td_first_name: string | null;
  td_last_name: string | null;
  td_email: string | null;
  td_role: string | null;

  // Campi preferred advisor
  pref_first_name: string | null;
  pref_last_name: string | null;
  pref_email: string | null;
  pref_role: string | null;
}

/**
 * Travel Request embedded nell'itinerario dashboard
 * Contiene solo i campi necessari per la visualizzazione
 */
export interface IEmbeddedTravelRequest {
  id: string;
  userId: string | null;
  destinations: string[];
  departurePoint: string | null;
  needsTransportation: boolean;
  totalGuests: number;
  guestsData: IGuest[];
  travelDatesFlexible: boolean;
  travelStartDate: string | null;
  travelEndDate: string | null;
  budgetMin: number | null;
  budgetMax: number | null;
  budgetFlexible: boolean;
  interests: string[];
  additionalNotes: string | null;
  contact: IContactInfo;
  status: TravelRequestStatus;
  assignedAdvisorId: string | null;
  preferredAdvisorId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Interfaccia per DashboardItinerary
 */
export interface IDashboardItinerary {
  // Identificatori
  id: string;
  travelRequestId: string;
  
  // Campi itinerario
  title: string;
  status: ItineraryStatus;
  startDate: string | null;
  endDate: string | null;
  guestsData: IGuest[];
  budget: number;
  description: string;
  image: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  
  // Totali e commissioni
  total: number | null;
  commisionableTotal: number | null;
  notCommisionableTotal: number | null;
  p2bCommissionPercentage: number | null;
  p2bCommissionAmount: number | null;
  tdFeePercentage: number | null;

  // Relazioni
  travelRequest: IEmbeddedTravelRequest;
  assignedAdvisor: IAdvisorInfo | null;
  preferredAdvisor: IAdvisorInfo | null;
}

/**
 * Classe DashboardItinerary
 * Rappresenta un itinerario completo con travel request e advisor info embedded
 */
export class DashboardItinerary implements IDashboardItinerary {
  // Identificatori
  id: string;
  travelRequestId: string;
  
  // Campi itinerario
  title: string;
  status: ItineraryStatus;
  startDate: string | null;
  endDate: string | null;
  guestsData: IGuest[];
  budget: number;
  description: string;
  image: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  
  // Totali e commissioni
  total: number | null;
  commisionableTotal: number | null;
  notCommisionableTotal: number | null;
  p2bCommissionPercentage: number | null;
  p2bCommissionAmount: number | null;
  tdFeePercentage: number | null;

  // Relazioni
  travelRequest: IEmbeddedTravelRequest;
  assignedAdvisor: AdvisorInfo | null;
  preferredAdvisor: AdvisorInfo | null;

  constructor(data: IDashboardViewData) {
    // Map itinerary fields
    this.id = data.itinerary_id;
    this.travelRequestId = data.travel_request_id;
    this.title = data.itinerary_title;
    this.status = data.itinerary_status as ItineraryStatus;
    this.startDate = data.itinerary_start_date;
    this.endDate = data.itinerary_end_date;
    this.guestsData = this.parseGuestsData(data.itinerary_guests_data);
    this.budget = data.itinerary_budget;
    this.description = data.itinerary_description;
    this.image = data.itinerary_image;
    this.note = data.itinerary_note;
    this.createdAt = data.itinerary_created_at;
    this.updatedAt = data.itinerary_updated_at;

    // Map totals
    this.total = data.itinerary_total;
    this.commisionableTotal = data.itinerary_commisionable_total;
    this.notCommisionableTotal = data.itinerary_not_commisionable_total;
    this.p2bCommissionPercentage = data.itinerary_p2b_commission_percentage;
    this.p2bCommissionAmount = data.itinerary_p2b_commission_amount;
    this.tdFeePercentage = data.itinerary_td_fee_percentage;

    // Map embedded travel request
    this.travelRequest = {
      id: data.travel_request_id,
      userId: data.travel_request_user_id,
      destinations: data.destinations || [],
      departurePoint: data.departure_point,
      needsTransportation: data.needs_transportation,
      totalGuests: data.total_guests,
      guestsData: this.parseGuestsData(data.guests_data),
      travelDatesFlexible: data.travel_dates_flexible,
      travelStartDate: data.travel_start_date,
      travelEndDate: data.travel_end_date,
      budgetMin: data.budget_min,
      budgetMax: data.budget_max,
      budgetFlexible: data.budget_flexible,
      interests: data.interests || [],
      additionalNotes: data.additional_notes,
      contact: {
        firstName: data.contact_first_name,
        lastName: data.contact_last_name,
        email: data.contact_email,
        phone: data.contact_phone,
      },
      status: data.travel_request_status as TravelRequestStatus,
      assignedAdvisorId: data.assigned_designer_id,
      preferredAdvisorId: data.travel_designer_preference,
      createdAt: data.travel_request_created_at,
      updatedAt: data.travel_request_updated_at,
    };

    // Map assigned advisor
    if (data.td_first_name && data.td_last_name && data.td_email) {
      this.assignedAdvisor = new AdvisorInfo({
        id: data.assigned_designer_id || undefined,
        firstName: data.td_first_name,
        lastName: data.td_last_name,
        email: data.td_email,
      });
    } else {
      this.assignedAdvisor = null;
    }

    // Map preferred advisor
    if (data.pref_first_name && data.pref_last_name && data.pref_email) {
      this.preferredAdvisor = new AdvisorInfo({
        id: data.travel_designer_preference || undefined,
        firstName: data.pref_first_name,
        lastName: data.pref_last_name,
        email: data.pref_email,
      });
    } else {
      this.preferredAdvisor = null;
    }
  }

  /**
   * Helper per parsare guests_data da Json
   */
  private parseGuestsData(data: Json): IGuest[] {
    if (Array.isArray(data)) {
      return data as unknown as IGuest[];
    }
    return [];
  }

  // ============ COMPUTED PROPERTIES ============

  get detailedStatus(): string {
    if (this.status === 'completed' && this.startDate && this.endDate) {
      const now = new Date();
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);
      
      if (now < start) {
        return 'upcoming';
      } else if (now >= start && now <= end) {
        return 'ongoing';
      } else {
        return 'past';
      }
    }
    return this.status;
  }

  /**
   * Nome completo del contatto
   */
  get contactFullName(): string {
    return `${this.travelRequest.contact.firstName} ${this.travelRequest.contact.lastName}`.trim();
  }

  /**
   * Email del contatto (shorthand)
   */
  get contactEmail(): string {
    return this.travelRequest.contact.email;
  }

  /**
   * Nome completo del travel advisor assegnato
   */
  get assignedAdvisorFullName(): string | null {
    return this.assignedAdvisor?.fullName || null;
  }

  /**
   * Nome completo del travel advisor preferito
   */
  get preferredAdvisorFullName(): string | null {
    return this.preferredAdvisor?.fullName || null;
  }

  /**
   * Check se l'itinerario è in stato draft
   */
  get isDraft(): boolean {
    return this.status === 'draft';
  }

  /**
   * Check se l'itinerario è in stato pending (attende approvazione)
   */
  get isPending(): boolean {
    return this.status === 'pending';
  }

  /**
   * Check se l'itinerario è approvato
   */
  get isApproved(): boolean {
    return this.status === 'approved';
  }

  /**
   * Check se l'itinerario è stato inviato
   */
  get isSended(): boolean {
    return this.status === 'sended';
  }

  /**
   * Check se l'itinerario è completato
   */
  get isCompleted(): boolean {
    return this.status === 'completed';
  }

  /**
   * Check se l'itinerario è rifiutato
   */
  get isRejected(): boolean {
    return this.status === 'rejected';
  }

  /**
   * Check se l'itinerario ha richiesto modifiche
   */
  get isRequestChanges(): boolean {
    return this.status === 'request_changes';
  }

  /**
   * Check se l'itinerario è scaduto
   */
  get isExpired(): boolean {
    return this.status === 'expired';
  }

  get isBlurred(): boolean {
    return this.isPending || this.isExpired || this.isRejected || this.isRequestChanges;
  }

  /**
   * Check se l'advisor assegnato è quello preferito
   */
  get hasPreferredAdvisorAssigned(): boolean {
    if (!this.assignedAdvisor || !this.preferredAdvisor) return false;
    return this.assignedAdvisor.email === this.preferredAdvisor.email;
  }

  /**
   * Numero totale di giorni dell'itinerario
   */
  get totalDays(): number {
    if (!this.startDate || !this.endDate) return 0;
    const start = new Date(this.startDate);
    const end = new Date(this.endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  /**
   * Numero totale di ospiti
   */
  get totalGuests(): number {
    return this.guestsData.length || this.travelRequest.totalGuests || 0;
  }

  /**
   * Destinazioni (shorthand da travel request)
   */
  get destinations(): string[] {
    return this.travelRequest.destinations;
  }

  /**
   * Prima destinazione o stringa vuota
   */
  get primaryDestination(): string {
    return this.travelRequest.destinations[0] || '';
  }
}
